/**
 * Google Search Console domain verification, through spark-creatives.
 *
 * For a domain we bought for the owner, its DNS zone is managed on our side (Lumio Relay), so the owner
 * can paste the TXT record Google shows for a Domain property and we publish it at the apex. Domains the
 * owner brought themselves (connected via Entri) are not ours to write: the owner adds the record at their
 * own registrar. spark-creatives checks that the signed-in owner owns the brand and resolves the brand's
 * domain itself, so the calls below only ever touch the active brand's own domain.
 *
 *   GET  {creatives}/creatives/tenant/{tenant}/search-console          → SearchConsoleStatus
 *   POST {creatives}/creatives/tenant/{tenant}/search-console/records  { value } → SearchConsoleStatus
 *
 * Refusals answer `{ "error": "<bare code>" }` (see `SearchConsoleErrorCode`).
 */
import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase, creativesServiceConfigured } from '../creatives/creative-api';

/** Why the panel cannot write records itself ('' when it can). */
export type SearchConsoleReason = '' | 'no_domain' | 'not_managed' | 'unavailable';

export type SearchConsoleRecord = {
  /** The full TXT value, `google-site-verification=<token>`. */
  value: string;
  /** Visible in public DNS right now. */
  live: boolean;
};

export type SearchConsoleStatus = {
  /** The brand's apex domain ('' when it has none of its own). */
  domain: string;
  /** We manage this domain's DNS, so records can be added here. */
  eligible: boolean;
  reason: SearchConsoleReason;
  records: SearchConsoleRecord[];
  max_records: number;
};

/**
 * A bare error code: one the service refuses with (`search_console_invalid_token`, `search_console_wrong_method`,
 * `search_console_no_domain`, `search_console_not_managed`, `search_console_too_many`,
 * `search_console_unavailable`), or one of the two this module adds for answers it could not read:
 * `unexpected` (the service answered with something that is not the expected JSON) and `network` (no answer).
 */
export type SearchConsoleErrorCode = string;

/** The service refused or failed; `code` is the bare code from the body, `status` the HTTP status (0 = no answer). */
export class SearchConsoleError extends Error {
  readonly code: SearchConsoleErrorCode;
  readonly status: number;
  constructor(code: SearchConsoleErrorCode, status: number) {
    super(code);
    this.name = 'SearchConsoleError';
    this.code = code;
    this.status = status;
  }
}

export const searchConsoleConfigured = creativesServiceConfigured;

/** Fallback when an answer leaves out `max_records`; mirrors the service's own cap. */
const DEFAULT_MAX_RECORDS = 10;

const routeBase = (tenantId: number): string =>
  `${creativesServiceBase()}/creatives/tenant/${encodeURIComponent(String(tenantId))}/search-console`;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

const REASONS: ReadonlyArray<SearchConsoleReason> = ['', 'no_domain', 'not_managed', 'unavailable'];

const toReason = (v: unknown): SearchConsoleReason => {
  const found = REASONS.find((reason) => reason === v);
  return found ?? 'unavailable';
};

const toRecord = (v: unknown): SearchConsoleRecord | null => {
  if (!isRecord(v) || typeof v.value !== 'string' || !v.value) return null;
  return { value: v.value, live: v.live === true };
};

const toStatus = (data: Record<string, unknown>): SearchConsoleStatus => {
  const eligible = data.eligible === true;
  const records = Array.isArray(data.records)
    ? data.records.map(toRecord).filter((r): r is SearchConsoleRecord => r !== null)
    : [];
  const max = typeof data.max_records === 'number' && data.max_records > 0 ? data.max_records : DEFAULT_MAX_RECORDS;
  return {
    domain: typeof data.domain === 'string' ? data.domain : '',
    eligible,
    // An eligible answer has no reason; an ineligible one without a known reason reads as "could not check".
    reason: eligible ? '' : toReason(data.reason),
    records,
    max_records: max,
  };
};

/** A 2xx answer that is not a JSON object (a SPA fallback page when the dev proxy or the service is down) is an
 *  error, not a status. */
const answerBody = (res: { status: number; data: unknown }): Record<string, unknown> => {
  if (res.status < 200 || res.status >= 300) {
    const code =
      isRecord(res.data) && typeof res.data.error === 'string' && res.data.error ? res.data.error : 'unexpected';
    throw new SearchConsoleError(code, res.status);
  }
  if (!isRecord(res.data)) throw new SearchConsoleError('unexpected', res.status);
  return res.data;
};

/** Run one request; a request that never got an answer becomes a `network` SearchConsoleError. */
const send = async (request: () => Promise<{ status: number; data: unknown }>): Promise<SearchConsoleStatus> => {
  let res: { status: number; data: unknown };
  try {
    res = await request();
  } catch {
    throw new SearchConsoleError('network', 0);
  }
  return toStatus(answerBody(res));
};

/** The brand's domain, whether we can write its DNS, and the Google verification records already there. */
export const getSearchConsole = (tenantId: number, signal?: AbortSignal): Promise<SearchConsoleStatus> =>
  send(() => axios.get<unknown>(routeBase(tenantId), creativesRequestConfig(signal)));

/** Publish one `google-site-verification=…` TXT value at the domain's apex; answers the fresh status. Adding a value
 *  that is already there is a no-op. */
export const addSearchConsoleRecord = (
  tenantId: number,
  value: string,
  signal?: AbortSignal,
): Promise<SearchConsoleStatus> =>
  send(() => axios.post<unknown>(`${routeBase(tenantId)}/records`, { value }, creativesRequestConfig(signal)));

export type NormalisedVerificationValue =
  | { ok: true; value: string }
  | { ok: false; code: 'search_console_invalid_token' | 'search_console_wrong_method' };

const PREFIX = 'google-site-verification=';
const TOKEN = /^[A-Za-z0-9_-]{20,128}$/;

/**
 * Client-side pre-check of what the owner pasted, mirroring the service's rules (the service stays
 * authoritative): trim, drop surrounding quotes, refuse the HTML-file and meta-tag methods, drop a
 * leading `google-site-verification=` (any case), then the remaining token must look like a Google token.
 */
export const normaliseVerificationValue = (input: string): NormalisedVerificationValue => {
  // Quotes come off both ends in any number and mix, exactly as the service trims them.
  const value = input
    .trim()
    .replace(/^["']+|["']+$/g, '')
    .trim();
  const lower = value.toLowerCase();
  if (lower.includes('<meta') || lower.endsWith('.html') || lower.startsWith('google-site-verification:')) {
    return { ok: false, code: 'search_console_wrong_method' };
  }
  const token = lower.startsWith(PREFIX) ? value.slice(PREFIX.length) : value;
  if (!TOKEN.test(token)) return { ok: false, code: 'search_console_invalid_token' };
  return { ok: true, value: `${PREFIX}${token}` };
};
